import { Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { formatGoalRealYield } from "web_myplann/Goals/Helpers/formatGoalRealYield";
import { RentDrawdownMode } from "web_myplann/Goals/Types/RentDrawdownMode";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InspirationCapitalCard } from "web_myplann/Inspiration/Components/InspirationCapitalCard";
import { InspirationSection } from "web_myplann/Inspiration/Components/InspirationSection";
import { InspirationToneNote } from "web_myplann/Inspiration/Components/InspirationToneNote";
import { RetirementDrawdownList } from "web_myplann/Inspiration/Retirement/Components/RetirementDrawdownList";
import { RETIREMENT_DRAWDOWN_YEARS } from "web_myplann/Inspiration/Retirement/constants";
import { useRetirementCapitalStages } from "web_myplann/Inspiration/Retirement/Hooks/useRetirementCapitalStages";
import type { RetirementContext } from "web_myplann/Inspiration/Retirement/Types/RetirementTypes";
import { formatInspirationMoney } from "web_myplann/Inspiration/Utils/inspirationMoney";

const R = Resources.Inspiration.Retirement;

type Props = {
	context: RetirementContext;
	onChangeDrawdownMode: (mode: RentDrawdownMode) => void;
};

export const RetirementCapitalSection: FC<Props> = ({
	context,
	onChangeDrawdownMode,
}) => {
	const { t } = useResource();
	const theme = useTheme();
	const stages = useRetirementCapitalStages(context);

	const covered = context.isCoveredByState;

	return (
		<InspirationSection
			title={t(covered ? R.capitalCoveredTitle : R.capitalTitle)}
			gap={theme.spacingTokens.lg}
		>
			{!covered && (
				<RetirementDrawdownList
					selectedMode={context.drawdownMode}
					onSelect={onChangeDrawdownMode}
				/>
			)}

			<Stack gap={covered ? theme.spacingTokens.md : theme.spacingTokens.sm}>
				<InspirationCapitalCard
					rows={[
						{
							label: t(R.rowIncome),
							value: formatInspirationMoney(context.desiredIncome),
						},
						{
							label: t(R.rowState),
							value: `− ${formatInspirationMoney(context.statePension)}`,
							valueColor: theme.palette.green.main,
						},
					]}
					remaining={
						covered
							? undefined
							: {
									label: t(R.rowGap),
									value: formatInspirationMoney(context.gap),
									suffix: t(R.perMonth),
								}
					}
					stages={covered ? undefined : stages}
					capital={
						covered
							? undefined
							: {
									label: t(R.capitalLabel),
									value: formatInspirationMoney(context.targetCapital),
									note:
										context.drawdownMode === RentDrawdownMode.Perpetual
											? t(R.capitalNotePerpetual, {
													rate: formatGoalRealYield(),
												})
											: t(R.capitalNoteToZero, {
													years: RETIREMENT_DRAWDOWN_YEARS,
												}),
								}
					}
				/>

				{covered ? (
					<InspirationToneNote color="green" text={R.capitalCoveredNote} />
				) : (
					<Typography variant="hint" color={theme.palette.grey[500]} relaxed>
						{t(R.capitalAssumptions, { rate: formatGoalRealYield() })}
					</Typography>
				)}
			</Stack>
		</InspirationSection>
	);
};
